import type { BusyInterval } from "./calendar";
import type { Person, PlannerState } from "./share";

export type RoomPerson = {
  memberId: string;
  city: string;
  country: string;
  province: string;
  iana: string;
  lat: number;
  lng: number;
  color: string;
  busy: BusyInterval[];
  displayName: string;
  ready: boolean;
};

export type RoomSnapshot = {
  createdAt: number;
  expiresAt: number;
  usedAt: number | null;
  date: string;
  duration: 15 | 30 | 45 | 60;
  selectedHour: number | null;
  finalized: boolean;
  people: RoomPerson[];
};

const MEMBER_KEY = "overlap_member";

export function myMemberId(): string {
  let id = sessionStorage.getItem(MEMBER_KEY);
  if (!id) {
    id = crypto.randomUUID();
    sessionStorage.setItem(MEMBER_KEY, id);
  }
  return id;
}

export function roomIdFromPath(): string | null {
  const match = location.pathname.match(/^\/r\/([A-Za-z0-9_-]{16,32})$/);
  return match?.[1] ?? null;
}

export function roomUrl(id: string): string {
  return `${location.origin}/r/${id}`;
}

export function personToRoom(person: Person, memberId: string): RoomPerson {
  return {
    memberId,
    city: person.city,
    country: person.country,
    province: person.province,
    iana: person.iana,
    lat: person.lat,
    lng: person.lng,
    color: person.color,
    busy: person.busy ?? [],
    displayName: (person.displayName ?? "").slice(0, 40),
    ready: Boolean(person.ready),
  };
}

export function roomToPeople(room: RoomSnapshot, me: string): Person[] {
  return room.people.map((p) => ({
    city: p.city,
    country: p.country,
    province: p.province,
    iana: p.iana,
    lat: p.lat,
    lng: p.lng,
    pop: 0,
    aliases: [],
    color: p.color,
    isYou: p.memberId === me,
    memberId: p.memberId,
    busy: p.busy ?? [],
    displayName: p.displayName,
    ready: p.ready,
  }));
}

export function applyRoom(state: PlannerState, room: RoomSnapshot, me: string): PlannerState {
  const people = roomToPeople(room, me);
  return {
    ...state,
    people: people.length ? people : state.people,
    date: room.date,
    duration: room.duration,
    selectedHour: room.selectedHour,
    activeId: state.activeId,
    busy: people.flatMap((p) => p.busy ?? []),
    finalized: room.finalized,
  };
}

export async function createRoom(state: PlannerState): Promise<{ id: string; room: RoomSnapshot }> {
  const me = myMemberId();
  const people = state.people.map((p, i) => personToRoom(p, p.memberId || (i === 0 ? me : crypto.randomUUID())));
  const res = await fetch("/api/rooms", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      date: state.date,
      duration: state.duration,
      selectedHour: state.selectedHour,
      people,
    }),
  });
  if (!res.ok) throw new Error("Could not create a private room");
  return res.json() as Promise<{ id: string; room: RoomSnapshot }>;
}

export async function fetchRoom(id: string): Promise<RoomSnapshot> {
  const res = await fetch(`/api/rooms/${id}`);
  if (res.status === 410) throw new Error("expired");
  if (!res.ok) throw new Error("Room not found");
  const body = (await res.json()) as { room: RoomSnapshot };
  return body.room;
}

export async function patchRoom(
  id: string,
  patch: {
    date?: string;
    duration?: 15 | 30 | 45 | 60;
    selectedHour?: number | null;
    upsert?: RoomPerson;
    removeMemberId?: string;
    clearPeople?: boolean;
    finalized?: boolean;
  },
): Promise<RoomSnapshot> {
  const res = await fetch(`/api/rooms/${id}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (res.status === 410) throw new Error("expired");
  if (!res.ok) throw new Error("Could not update room");
  const body = (await res.json()) as { room: RoomSnapshot };
  return body.room;
}

export function connectRoom(id: string, onRoom: (room: RoomSnapshot) => void, onDead: () => void): () => void {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  const socket = new WebSocket(`${proto}//${location.host}/api/rooms/${id}/ws`);
  socket.addEventListener("message", (event) => {
    try {
      const msg = JSON.parse(String(event.data)) as { type?: string; room?: RoomSnapshot };
      if (msg.type === "state" && msg.room) onRoom(msg.room);
    } catch {
      /* ignore */
    }
  });
  socket.addEventListener("close", (event) => {
    if (event.code === 4001) onDead();
  });
  return () => socket.close();
}
